import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  InputLabel,
  Typography,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { CustomFormContext } from "../../CustomForm";
import { CheckCircleOutlineOutlined, CloseOutlined } from "@mui/icons-material";
import { prepareContent } from "../../helpers";
import { ErrorInfo, Validation, Option } from "../../types";
import React from "react";
import { FieldTypeId } from "../../../../lib/enums";
import CheckIcon from "@mui/icons-material/Check";

interface CustomCheckboxProps<T extends Option> {
  nonForm?: boolean;
  alias: string;
  fieldId: number;
  header?: string;
  localized?: boolean;
  fieldOption?: T | number | string | null;
  valueProp?: string;
  labelProp?: string;
  readOnlyField?: boolean;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
  validations?: Validation[];
  typeId: FieldTypeId;
}

const CustomCheckbox = <T extends Option>({
  nonForm = false,
  alias,
  fieldId,
  valueProp,
  labelProp,
  header = "",
  required = false,
  fieldOption,
  readOnlyField = false,
  disabled = false,
  className = "",
  style = {},
  typeId,
}: CustomCheckboxProps<T>) => {
  const {
    fieldValue,
    dataIsSet,
    getValidationErrors,
    setDataDispatch,
    readOnlyState,
    errorsAreSet,
    tempData,
    showErrors,
  } = useContext(CustomFormContext);
  const [value, setValue] = useState<T | number | string | boolean | null>(
    null
  );
  const [errors, setErrors] = useState<ErrorInfo[] | null>(null);

  useEffect(() => {
    if (!dataIsSet || !errorsAreSet) {
      return;
    }
    const data = fieldValue(alias);
    if (data === undefined) {
      setValue(null);
      return;
    }
    const errors = getValidationErrors(alias);
    setValue(data);
    if (errors) {
      setErrors(errors);
    }
  }, [dataIsSet, errorsAreSet, tempData]);

  const getValue = () => {
    if (fieldOption) {
      if (valueProp && fieldOption?.hasOwnProperty(valueProp)) {
        return (fieldOption as any)[valueProp];
      }
      return fieldOption;
    }
    return true;
  };

  const handleSave = () => {
    const data = prepareContent(value, alias, nonForm);
    setDataDispatch({
      type: "UPDATE_CONTENT",
      payload: data,
    });
  };

  useEffect(() => {
    handleSave();
  }, [value]);

  useEffect(() => {
    setErrors(getValidationErrors(alias));
  }, [getValidationErrors, alias]);

  return readOnlyField || readOnlyState ? (
    <Grid
      item
      xs={12}
      sm={6}
      lg={4}
      sx={{
        opacity: value ? 1 : 0.4,
        cursor: "not-allowed",
      }}
      flexDirection="row"
    >
      <Grid item />
      <Grid container flexDirection="row">
        <Grid item>
          {value ? (
            <CheckCircleOutlineOutlined color="success" />
          ) : (
            <CloseOutlined color="error" />
          )}
        </Grid>
        <Grid item>
          <Typography
            sx={{
              marginLeft: 1,
              fontWeight: value ? "700" : "400",
            }}
          >
            {header} {required && "*"}{" "}
          </Typography>
        </Grid>
      </Grid>
    </Grid>
  ) : (
    <Grid item xs={12} sm={6} lg={4} className={className} style={style}>
      <FormGroup>
        <FormControlLabel
          disabled={disabled}
          data-testid={alias}
          control={
            <Checkbox
              name={alias}
              onClick={(e: any) =>
                setValue(e.target.checked ? getValue() : null)
              }
              checked={value ? true : false}
              checkedIcon={<CheckIcon sx={{ color: "#26a69a" }} />}
            />
          }
          label={
            <InputLabel>
              {header}{" "}
              {(required ||
                errors?.find((a) => a.error === "isNotNullOrEmpty")) &&
                "*"}{" "}
            </InputLabel>
          }
        />
      </FormGroup>

      {showErrors &&
        errors?.map((info, index) => (
          <FormHelperText key={index} variant="standard">
            <Typography variant="caption" color="red">
              {info.message}
            </Typography>
          </FormHelperText>
        ))}
    </Grid>
  );
};

CustomCheckbox.fieldType = "CustomCheckbox";

export default CustomCheckbox;
